import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, Chip, FormControlLabel, Stack, Switch, Typography} from '@mui/material';
import {newOwnerSignals,readableOwnerReplies,safeOwnerSpeech} from './OwnerVoiceMonitor.logic.mjs';

/**
 * PATCH A.5 — Voice monitor in Owner Live Viewer.
 * Authority: authenticated Owner group projection; backend classifies provider-accepted replies.
 * No AI prompts, no WhatsApp sends, no Mongo writes, no persistence of voice permission.
 * Voice and tones require explicit per-tab enablement and run only while visible/live/unpaused.
 * Browser speech synthesis availability/language depends on the device and browser.
 */
const formatTime=value=>{const d=new Date(value);return Number.isFinite(d.getTime())?d.toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'}):'—';};
const isVisible=()=>typeof document!=='undefined'&&document.visibilityState==='visible';

export default function OwnerVoiceMonitor({ownerGroup,events=[],error='',paused=false,live=true}) {
 const [voice,setVoice]=useState(false),[sounds,setSounds]=useState(false),[notice,setNotice]=useState('');
 const [speaking,setSpeaking]=useState(false);
 const [voiceAvailable,setVoiceAvailable]=useState(typeof window!=='undefined'&&'speechSynthesis' in window&&'SpeechSynthesisUtterance' in window);
 const replies=useMemo(()=>readableOwnerReplies(ownerGroup),[ownerGroup]);
 const latest=replies[replies.length-1];
 const refs=useRef({replyIds:new Set(),eventIds:new Set(),hydratedReplies:false,hydratedEvents:false,ctx:null});
 const opts=useRef({voice:false,sounds:false,paused:false,live:true,error:'',visible:true});
 opts.current={voice,sounds,paused,live,error,visible:isVisible()};

 const cancelSpeech=useCallback(()=>{
   if(typeof window!=='undefined' && window.speechSynthesis)window.speechSynthesis.cancel();
   setSpeaking(false);
 },[]);
 const tone=useCallback(kind=>{
   if(!opts.current.sounds||!opts.current.live||opts.current.paused||opts.current.error||!isVisible())return;
   try{
     const Context=window.AudioContext||window.webkitAudioContext;
     if(!Context)return;
     const ctx=refs.current.ctx || new Context();refs.current.ctx=ctx;
     if(ctx.state==='suspended'){void ctx.resume().catch(()=>{});return;}
     const osc=ctx.createOscillator(),volume=ctx.createGain();
     const base=kind==='thinking'?440:kind==='incoming'?540:670;
     const now=ctx.currentTime;
     osc.type='sine';osc.frequency.setValueAtTime(base,now);
     osc.frequency.exponentialRampToValueAtTime(kind==='done'?790:base*1.1,now+.12);
     volume.gain.setValueAtTime(.0001,now);
     volume.gain.exponentialRampToValueAtTime(.014,now+.015);
     volume.gain.exponentialRampToValueAtTime(.0001,now+.15);
     osc.connect(volume);volume.connect(ctx.destination);osc.start(now);osc.stop(now+.16);
   }catch{ /* AudioContext is optional and must not affect Owner */ }
 },[]);
 const speak=useCallback((reply,{explicit=false}={})=>{
   if(!reply?.text)return;
   if(!explicit && (!opts.current.voice||!opts.current.live||opts.current.paused||opts.current.error||!isVisible()))return;
   if(!voiceAvailable){setNotice('Este navegador no tiene lectura de voz disponible.');return;}
   try{
     const synthesis=window.speechSynthesis;
     synthesis.cancel();
     const utterance=new window.SpeechSynthesisUtterance(safeOwnerSpeech(reply.text));
     utterance.lang='es-AR';utterance.rate=.96;utterance.pitch=1;utterance.volume=.9;
     const voices=synthesis.getVoices();
     const spanish=voices.find(v=>v.lang?.toLowerCase()==='es-ar')||voices.find(v=>v.lang?.toLowerCase().startsWith('es-'));
     if(spanish)utterance.voice=spanish;
     utterance.onstart=()=>setSpeaking(true);
     utterance.onend=()=>setSpeaking(false);
     utterance.onerror=(ev)=>{setSpeaking(false);if(ev.error!=='canceled'&&ev.error!=='interrupted')setNotice('La voz no se pudo reproducir en este navegador.');};
     synthesis.speak(utterance);
   }catch{setSpeaking(false);setNotice('El navegador bloqueó la lectura en voz alta.');}
 },[voiceAvailable]);

 // First poll is only a baseline; opening the page or re-enabling voice must NOT replay history.
 useEffect(()=>{
   if(!ownerGroup?.configured||error)return;
   const ref=refs.current;
   const result=newOwnerSignals(ref.replyIds,replies,{initialized:ref.hydratedReplies});
   ref.replyIds=result.ids;ref.hydratedReplies=true;
   if(!opts.current.live||opts.current.paused||!isVisible())return;
   const fresh=result.newItems.filter(r=>Math.abs(Date.now()-Date.parse(r.at))<60000);
   if(fresh.length){tone('done');if(opts.current.voice)speak(fresh[fresh.length-1]);}
 },[ownerGroup,replies,error,live,paused,tone,speak]);

 useEffect(()=>{
   if(error)return;
   const ref=refs.current;
   const result=newOwnerSignals(ref.eventIds,events,{initialized:ref.hydratedEvents});
   ref.eventIds=result.ids;ref.hydratedEvents=true;
   if(!opts.current.live||opts.current.paused||!isVisible()||!result.newItems.length)return;
   // A single quiet cue per new polling batch; do not interpret as hidden thoughts.
   const stages=result.newItems.filter(e=>Math.abs(Date.now()-Date.parse(e.at))<60000).map(e=>String(e.stage||'').toUpperCase());
   if(stages.some(s=>s.includes('AI_INTERPRETATION_STARTED')||s.includes('AI_PENDING')))tone('thinking');
   else if(stages.some(s=>s.includes('TURN_RECEIVED')))tone('incoming');
 },[events,error,live,paused,tone]);

 useEffect(()=>{
   if(!voice||paused||!live||error)cancelSpeech();
 },[voice,paused,live,error,cancelSpeech]);
 useEffect(()=>{
   const hidden=()=>{if(!isVisible())cancelSpeech();};
   if(typeof document!=='undefined')document.addEventListener('visibilitychange',hidden);
   return ()=>{
     if(typeof document!=='undefined')document.removeEventListener('visibilitychange',hidden);
     if(typeof window!=='undefined' && window.speechSynthesis)window.speechSynthesis.cancel();
     const ctx=refs.current.ctx;refs.current.ctx=null;
     if(ctx)void ctx.close().catch(()=>{});
   };
 },[cancelSpeech]);

 const enableVoice=(enabled)=>{
   if(enabled&&!voiceAvailable){setNotice('No hay sintetizador de voz en este navegador.');return;}
   // Unlock browser speech through a real click. Do not read existing replies automatically.
   setNotice('');setVoice(enabled);
   if(!enabled)cancelSpeech();
 };
 const enableSounds=(enabled)=>{
   setSounds(enabled);
   if(enabled){try{const Context=window.AudioContext||window.webkitAudioContext;
     if(Context){const ctx=refs.current.ctx||new Context();refs.current.ctx=ctx;void ctx.resume().catch(()=>{});}
   }catch{setNotice('Este navegador no admite los sonidos.');}}
 };
 return <Box sx={{mt:2,textAlign:'left',p:1.4,border:'1px solid',borderColor:'divider',borderRadius:2}}>
   <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
     <Typography variant="subtitle2" fontWeight={800}>Owner habla · respuestas verificadas</Typography>
     <Chip size="small" variant="outlined" label={speaking?'Leyendo…':live&&!paused?'En escucha':'En pausa'}/>
   </Stack>
   <Typography variant="caption" color="text.secondary" sx={{display:'block',mb:1}}>Lectura de respuestas enviadas al proveedor; no reproduce tus propios mensajes ni pensamientos internos.</Typography>
   <Box aria-live="polite" sx={{p:1,mb:1,backgroundColor:'action.hover',borderRadius:1.5,minHeight:72}}>
     {error?<Typography variant="body2" color="warning.main">Sin actualización del grupo; se conserva lo último observado.</Typography>:!ownerGroup?.configured?<Typography variant="body2" color="text.secondary">Seleccioná un grupo Owner en WhatsApp para visualizar las respuestas.</Typography>:latest?<><Typography variant="caption" color="text.secondary">{formatTime(latest.at)} · {latest.kind==='companion'?'Acompañamiento':'Respuesta de Owner'} · aceptada por el proveedor</Typography><Typography variant="body2" sx={{whiteSpace:'pre-wrap',overflowWrap:'anywhere',maxHeight:170,overflowY:'auto',mt:.4}}>{latest.text}</Typography></>:<Typography variant="body2" color="text.secondary">Todavía no hay respuestas de Owner con envío confirmado en esta ventana.</Typography>}
   </Box>
   <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
     <FormControlLabel sx={{m:0}} control={<Switch size="small" checked={voice} onChange={(_,checked)=>enableVoice(checked)} disabled={!voiceAvailable}/>} label={<Typography variant="body2">Voz</Typography>}/>
     <FormControlLabel sx={{m:0}} control={<Switch size="small" checked={sounds} onChange={(_,checked)=>enableSounds(checked)}/>} label={<Typography variant="body2">Sonidos suaves</Typography>}/>
     <Button size="small" variant="outlined" onClick={()=>speak(latest,{explicit:true})} disabled={!latest||!voiceAvailable}>Escuchar último</Button>
     <Button size="small" variant="text" onClick={cancelSpeech} disabled={!speaking}>Detener</Button>
   </Stack>
   <Typography variant="caption" color="text.secondary" sx={{display:'block',mt:.5}}>Opcional, solo en esta pestaña. Voz del navegador, sin micrófono. Actualización aproximada cada 5 segundos.</Typography>
   {notice&&<Alert severity="info" sx={{mt:1}} onClose={()=>setNotice('')}>{notice}</Alert>}
 </Box>;
}
